import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FlecheEnvoiClair from '../../assets/fleche_envoi_clair.png';
import styled from 'styled-components';
import theme from '../../theme';
import {
  Container,
  ContentWrapper,
  ReturnButton,
  LogoImage,
  ChatBubbleContainer,
  ChatText,
  LoadingOverlay,
  LogoBubbleWrapper,
  SubmitButton,
  OptionButton,
  OptionsScrollContainer as BaseOptionsScrollContainer,
  OptionsContainer,
  ButtonsWrapper,
} from './styles';
import ProgressBar from '../ProgressBar';
import useDeviceType from '../useDeviceType';

import compassImage from '../../assets/meo_write.png';

// New wrapper to center the options block (similar to Choice.js)
const OptionsCenteringWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center; /* Vertical centering */
  align-items: center;   /* Horizontal centering (for the container itself) */
  flex-grow: 1;          /* Take available vertical space */
  width: 100%;
  padding-bottom: 40px; /* Add padding to avoid overlap with fixed submit button */
`;

// New styled component to override the top value
const StyledYearOptionsScrollContainer = styled(BaseOptionsScrollContainer)`
  top: ${props => props.isMobile ? '25%' : '25%'}; // Adjusted top values
`;

const YearStudy = () => {
  const navigate = useNavigate();
  const { isMobile } = useDeviceType();
  const [studyYear, setStudyYear] = useState(''); // Will store "1", "2", etc.
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(true);
  const [text, setText] = useState('');
  const fullText = "En quelle année d'études es-tu ?";
  const [submitActive, setSubmitActive] = useState(false);
  const [activeOption, setActiveOption] = useState(null); // For button press effect

  const yearOptions = Array.from({ length: 12 }, (_, i) => ({
    value: (i + 1).toString(),
    label: `Bac + ${i + 1}`,
  }));
  // Add "Terminale" as an option at the beginning if needed, or adjust range.
  // For now, sticking to "Bac + 1" to "Bac + 12" as per request.

  useEffect(() => {
    let currentIndex = 0;
    const typingInterval = setInterval(() => {
      if (currentIndex <= fullText.length) {
        setText(fullText.slice(0, currentIndex));
        currentIndex++;
      } else {
        setIsTyping(false);
        clearInterval(typingInterval);
        // No input to focus anymore
      }
    }, 15);
    return () => clearInterval(typingInterval);
  }, []);

  const handleOptionClick = (option) => {
    setStudyYear(option.value);
  };

  const isValidYear = () => {
    return studyYear !== ''; // Check if an option has been selected
  };

  const handleSubmit = () => {
    if (!isValidYear() || isTyping) return;
    // Potentially save to backend if needed here or in a later step
    localStorage.setItem('studyYear', studyYear);
    navigate('/lycee'); 
  };

  const handleBack = () => {
    navigate('/bac1');
  };

  // Submit Button Handlers
  const handleSubmitPressStart = () => {
    if (isLoading || isTyping || !isValidYear()) return;
    setSubmitActive(true);
  };

  const handleSubmitPressEnd = () => {
    if (isLoading || isTyping || !isValidYear()) return;
    setSubmitActive(false);
    setTimeout(() => {
      handleSubmit();
    }, 150);
  };

  return (
    <Container>
      {isLoading && <LoadingOverlay isLoading={isLoading} />}
      <ReturnButton onClick={handleBack}>
        <img src={FlecheEnvoiClair} alt="Back" style={{ width: '24px', height: '24px' }} />
      </ReturnButton>
      <ContentWrapper>
        <ProgressBar startPercent={45} endPercent={75} />
        <LogoBubbleWrapper>
          <LogoImage src={compassImage} alt="Compass Logo" />
          <ChatBubbleContainer>
            <ChatText isVisible={text.length > 0}>
              <span>{text}</span>
            </ChatText>
          </ChatBubbleContainer>
        </LogoBubbleWrapper>

        <OptionsCenteringWrapper>
          <StyledYearOptionsScrollContainer isMobile={isMobile}>
            <OptionsContainer>
              <ButtonsWrapper style={{
                opacity: isTyping ? 0 : 1,
                transition: 'opacity 0.3s ease',
                pointerEvents: isTyping ? 'none' : 'auto'
              }}>
                {yearOptions.map((option) => (
                  <OptionButton
                    key={option.value}
                    $isSelected={studyYear === option.value}
                    type="button"
                    aria-pressed={studyYear === option.value}
                    onMouseDown={() => setActiveOption(option.value)}
                    onMouseUp={() => { setActiveOption(null); handleOptionClick(option); }}
                    onMouseLeave={() => setActiveOption(null)}
                    onTouchStart={() => setActiveOption(option.value)}
                    onTouchEnd={() => { setActiveOption(null); handleOptionClick(option); }}
                    active={activeOption === option.value}
                  >
                    {/* No OptionIcon needed for year labels */}
                    {option.label}
                  </OptionButton>
                ))}
              </ButtonsWrapper>
            </OptionsContainer>
          </StyledYearOptionsScrollContainer>
        </OptionsCenteringWrapper>

        <SubmitButton
          type="button"
          disabled={isLoading || isTyping || !isValidYear()}
          style={{ cursor: (isTyping || !isValidYear()) ? 'not-allowed' : 'pointer' }}
          onMouseDown={handleSubmitPressStart}
          onMouseUp={handleSubmitPressEnd}
          onMouseLeave={() => setSubmitActive(false)}
          onTouchStart={handleSubmitPressStart}
          onTouchEnd={handleSubmitPressEnd}
          active={submitActive}
        >
          {isLoading ? 'Chargement...' : 'Continuer'}
        </SubmitButton>
      </ContentWrapper>
    </Container>
  );
};

export default YearStudy; 